<template>
  <FlotoFixedView>
    <FlotoContentLoader :loading="loading">
      <template v-if="kb">
        <FlotoPageHeader
          use-divider
          class="px-4"
          :back-link="backLink || $currentModule.getRoute()"
          :title="`${kb.name}: ${kb.subject}`"
        >
          <template v-if="isApproval" v-slot:after-title>
            <slot name="actions"></slot>
          </template>
          <template v-else v-slot:after-title>
            <MCol>
              <MRow class="justify-end mb-1">
                <MButton
                  v-if="kb.status === 'draft'"
                  :loading="processing"
                  class="mx-1"
                  @click="publishKb"
                >
                  {{ $t('publish') }}
                </MButton>
                <MTooltip v-if="kb.status !== 'expired'">
                  <template v-slot:trigger>
                    <MButton
                      shape="circle"
                      variant="neutral-lighter"
                      class="mx-1"
                      :shadow="false"
                      @click="printKb"
                    >
                      <MIcon name="print" />
                    </MButton>
                  </template>
                  {{ $t('print') }}
                </MTooltip>
                <div v-if="!isPortalLogin" class="flex items-center">
                  <KnowledgeReviewScheduler
                    v-if="
                      canManageReview && !kb.trashed && kb.status !== 'expired'
                    "
                    :key="renderReviewScheduler"
                    :item="kb"
                    :resource-id="kb.id"
                    @refresh="refreshKb"
                  />
                  <MTooltip
                    v-if="kb.canEdit && !kb.trashed && kb.status !== 'expired'"
                  >
                    <template v-slot:trigger>
                      <FlotoLink
                        as-button
                        variant="neutral-lighter"
                        :shadow="false"
                        shape="circle"
                        class="mx-1"
                        :to="
                          $currentModule.getRoute('edit', {
                            params: { id: kb.id },
                          })
                        "
                      >
                        <MIcon name="pencil" />
                      </FlotoLink>
                    </template>
                    {{ $t('edit') }}
                  </MTooltip>

                  <FlotoDeleteBtn
                    v-if="kb.canDelete"
                    class="mx-1"
                    :message="
                      $t('confirm_delete_item', { item: $tc('knowledge') })
                    "
                    @confirm="deleteKb"
                  >
                    <MTooltip>
                      <template v-slot:trigger>
                        <MButton
                          variant="neutral-lighter"
                          :shadow="false"
                          shape="circle"
                        >
                          <MIcon name="trash-alt" class="text-secondary-red" />
                        </MButton>
                      </template>
                      {{ $t('delete') }}
                    </MTooltip>
                  </FlotoDeleteBtn>

                  <RestoreBtn
                    v-if="kb.canEdit && kb.trashed && kb.status !== 'expired'"
                    @confirm="handleRestoreKb"
                  />
                  <ActionMenu
                    :kb="kb"
                    :module-name="$currentModule.name"
                    :disabled="!kb.canEdit || kb.trashed"
                    class="mx-1"
                    @approval-changed="refreshKb"
                  />
                </div>
              </MRow>
              <MRow>
                <span>
                  <small v-if="!isApproval" class="text-neutral mx-2">
                    <template v-if="!isPortalLogin">
                      {{ $t('updated_time') }}:
                    </template>
                    {{ kb.updatedAt | datetime }} ({{ kb.updatedAt | timeago }})
                  </small>
                </span>
              </MRow>
            </MCol>
          </template>
          <div class="flex justify-between">
            <small v-if="!isPortalLogin" class="text-neutral">
              {{ $t('by') }}
              <FlotoUserDrawer :user-id="kb.owner" :disabled="isPortalLogin" />
              {{ $t('at') }}
              {{ kb.createdAt | datetime }}
              ({{ kb.createdAt | timeago }})
            </small>
          </div>
          <div v-if="!isPortalLogin" class="flex flex-col w-full my-2">
            <div class="flex flex-1">
              <div class="items-center flex min-w-0">
                <MTooltip class="inline-flex items-center" placement="topLeft">
                  <template v-slot:trigger>
                    <MIcon
                      name="folder-open"
                      size="lg"
                      class="mx-1 text-neutral-light"
                    />
                  </template>
                  {{ $tc('folder') }}
                </MTooltip>
                <div class="flex flex-col min-w-0">
                  <span class="text-ellipsis">
                    {{ kb.folderName }}
                  </span>
                </div>
              </div>
              <div class="mx-6 items-center flex">
                <MTooltip class="inline-flex items-center" placement="topLeft">
                  <template v-slot:trigger>
                    <MIcon
                      name="status"
                      size="2x"
                      class="mx-1 text-neutral-light"
                    />
                  </template>
                  {{ $t('status') }}
                </MTooltip>
                <FlotoDropdownPicker
                  :value="kb.status"
                  :options="statusOptions"
                  disabled
                />
              </div>
              <div class="mx-6 flex">
                <MTooltip class="inline-flex items-center">
                  <template v-slot:trigger>
                    <MIcon
                      name="book-open"
                      size="lg"
                      class="mx-1 text-neutral-light"
                    />
                    <h3 class="mb-0">{{ kb.totalRead }}</h3>
                  </template>
                  {{ $t('total_read') }}
                </MTooltip>
              </div>
              <div class="mx-6 items-center flex">
                <div class="flex justify-around flex-1">
                  <FeedbackCount
                    :positive-feedback="kb.positiveFeedback"
                    :negative-feedback="kb.negativeFeedback"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="kb.tags.length || (kb.approvalStatus && !isApproval)"
              class="my-2 flex"
            >
              <MIcon name="tags" class="mr-2" size="lg" />
              <ApprovalDetailsDrawer
                v-if="kb.approvalStatus && !isApproval"
                :kb="kb"
                :use-icon="false"
                :module-name="$currentModule.name"
                @done="refreshKb"
              >
                <template v-slot:trigger>
                  <ApprovalStatusIcon
                    :class="
                      kb.approvalStatus === 'pre_approved'
                        ? 'cursor-text flex'
                        : 'cursor-pointer flex'
                    "
                    :status="kb.approvalStatus"
                  >
                    <template v-slot:prefix-text>
                      {{ $tc('approval') }}:
                    </template>
                  </ApprovalStatusIcon>
                </template>
              </ApprovalDetailsDrawer>
              <FlotoTagsPicker :value="kb.tags" disabled />
            </div>
          </div>
          <div v-if="isApproval" class="my-2 flex">
            <MTag
              :closable="false"
              :confirmable="false"
              variant="neutral-lighter"
              class="cursor-text"
            >
              {{ $tc('approval') }} {{ $tc('requester') }} :
              <FlotoUserDrawer :user-id="approval.createdById" />
            </MTag>
          </div>
        </FlotoPageHeader>
        <FlotoScrollView>
          <MRow :gutter="0">
            <MCol :sm="8" :size="12">
              <div class="px-4">
                <div id="kb-display" class="printing-div">
                  <div class="flex flex-col mb-2 printing-only print-margin">
                    <h2 class="mb-0"> {{ kb.name }}: {{ kb.subject }} </h2>
                    <small>
                      {{ kb.createdAt | datetime }}
                      ({{ kb.createdAt | timeago }})
                    </small>
                  </div>
                  <MDivider class="printing-only" />
                  <FlotoRichEditor
                    class="print-margin"
                    :value="kb.content"
                    disabled
                  />
                </div>
              </div>
              <div
                v-if="
                  kb.status === 'published' &&
                  !kb.trashed &&
                  loggedIn &&
                  !isApproval
                "
                class="my-2 px-4"
              >
                <RegisterFeedback
                  :feedback-given="kb.hasGivenFeedback"
                  :feedback="kb.isHelpfulFeedback"
                  @like="handleLikeArticle"
                  @dislike="handleDisLikeArticle"
                  @removed="handleFeedbackRemoved"
                />
              </div>
            </MCol>
            <MCol
              :sm="4"
              :size="12"
              class="border-solid border-neutral-lighter border-l border-t-0 border-r-0 border-b-0"
            >
              <div class="px-2">
                <FlotoAttachment
                  :value="kb.fileAttachments"
                  disabled
                  class="flex-col"
                />
              </div>
              <div v-if="isApproval" class="my-4 px-4">
                <slot name="comments"></slot>
              </div>
              <div
                v-else-if="loggedIn"
                class="my-4 px-4"
                style="min-height: 120px"
              >
                <h6 class="text-primary font-normal">{{ $tc('review', 2) }}</h6>
                <MDivider />
                <ConversationContainer
                  :key="renderReviewScheduler"
                  :module-name="$constants.KNOWLEDGE"
                  :can-add-review="kb.ableToAddReview"
                  :apply-added-by-filter="!isPortalLogin"
                  :resource-id="kb.id"
                  :fetch-fn="fetchConversations"
                  @refresh="refreshKb"
                />
              </div>
            </MCol>
          </MRow>
        </FlotoScrollView>
      </template>
    </FlotoContentLoader>
  </FlotoFixedView>
</template>

<script>
import ConversationContainer from '@components/conversation/conversation-container'
import { getConversationsApi } from '@components/conversation/api'
import { KnowledgeStatusOptions } from '@utils/status'
import { authComputed } from '@state/modules/auth'
import { PreferenceComputed } from '@state/modules/preference'
import { printElement } from '@utils/print'
import { getApprovalResourceDetailApi } from '@modules/my-approval/my-approval-api'
import {
  readKnowledgeApi,
  removeKnowledgeApi,
  registerKnowledgeFeedbackApi,
  removeKnowledgeFeedbackApi,
  getKnowledgeApi,
  restoreKnowledgeApi,
  updateKnowledgeApi,
} from '../knowledge-api'
import FeedbackCount from '../components/feedback-count'
import ActionMenu from '../components/action-menu'
import RegisterFeedback from '../components/register-feedback'
import RestoreBtn from '../components/restore-btn'
import ApprovalStatusIcon from '@components/approval/components/approval-status-icon'
import ApprovalDetailsDrawer from '../components/approval-details-drawer'
import KnowledgeReviewScheduler from '../components/knowledge-review-scheduler.vue'

export default {
  name: 'Read',
  components: {
    FeedbackCount,
    ActionMenu,
    RegisterFeedback,
    RestoreBtn,
    ApprovalStatusIcon,
    ApprovalDetailsDrawer,
    KnowledgeReviewScheduler,
    ConversationContainer,
  },
  props: {
    id: { type: Number, default: undefined },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
    backLink: { type: Object, default: undefined },
    resourceId: { type: [String, Number], default: undefined },
  },
  data() {
    this.statusOptions = KnowledgeStatusOptions()
    return {
      loading: true,
      kb: null,
      processing: false,
      restoreOnFolder: undefined,
      defaultKb: {
        status: 'published',
        id: this.$route.params.id,
      },
      renderReviewScheduler: 1,
    }
  },
  computed: {
    ...authComputed,
    ...PreferenceComputed,
    canManageReview() {
      return this.myAllowedModules.indexOf('knowledge.can_manage_review') >= 0
    },
  },
  created() {
    this.getKnowledge()
  },
  methods: {
    fetchConversations(limit, offset, sortBy, filters) {
      const quals = {
        ...filters,
        sortBy,
      }
      if (this.isPortalLogin) {
        quals.createdById = this.user.id
      }
      return getConversationsApi(
        this.$constants.KNOWLEDGE,
        this.kb.id,
        quals,
        limit,
        offset
      )
    },
    refreshKb() {
      getKnowledgeApi(this.$route.params.id).then((data) => {
        this.kb = data
        this.renderReviewScheduler++
      })
    },
    getKnowledge() {
      if (this.isApproval) {
        getApprovalResourceDetailApi(
          this.$constants.KNOWLEDGE,
          this.resourceId,
          this.isPortalLogin
        ).then((data) => {
          this.kb = data
          this.$emit('resource', this.kb)
          this.loading = false
        })
      } else {
        readKnowledgeApi(this.$route.params.id || this.id, this.isPortalLogin)
          .then((data) => {
            this.kb = data
            this.$emit('resource', this.kb)
            this.loading = false
          })
          .catch(() => {
            this.$router.replace({ name: '404' })
          })
      }
    },
    deleteKb() {
      removeKnowledgeApi(this.kb.id).then(() => {
        this.$router.push(this.$currentModule.getRoute(''))
      })
    },
    handleLikeArticle() {
      registerKnowledgeFeedbackApi(this.kb.id, true, this.isPortalLogin).then(
        () => {
          this.refreshKb()
        }
      )
    },
    publishKb() {
      this.processing = true
      updateKnowledgeApi(this.defaultKb)
        .then((data) => {
          this.kb = data
        })
        .finally(() => {
          this.processing = false
        })
    },
    handleDisLikeArticle() {
      registerKnowledgeFeedbackApi(this.kb.id, false, this.isPortalLogin).then(
        () => {
          this.refreshKb()
        }
      )
    },
    handleFeedbackRemoved() {
      removeKnowledgeFeedbackApi(this.kb.id, this.isPortalLogin).then(() => {
        this.refreshKb()
      })
    },
    handleRestoreKb(folderId) {
      restoreKnowledgeApi(this.kb.id, folderId).then(() => this.refreshKb())
    },
    printKb() {
      printElement(
        document.getElementById('kb-display'),
        false,
        window,
        `${this.kb.name}: ${this.kb.subject}`
      )
    },
  },
}
</script>
